/**
 * The export e-mail's link `/settings/data?export=<id>#token=<token>` (API-OVERVIEW §11.5). Before the app signs
 * in, `captureExportToken` moves the one-time token out of the address bar into the reviewed transient store
 * (15 minutes, this tab only), so it never enters `returnTo`, the OIDC state or the history. The data page reads
 * it for its export and forgets it once the download succeeded or was refused.
 */
import { isUuid, transientStore } from "@brevalt/app-core";

export const EXPORT_TOKEN_TTL_MS = 15 * 60_000;
const TOKEN = /(?:^#|&)token=([\w.~-]{8,512})(?:&|$)/;

export function captureExportToken(location: Pick<Location, "pathname" | "search" | "hash"> = window.location): void {
  if (!location.hash.includes("token=")) return;
  const token = TOKEN.exec(location.hash)?.[1];
  const exportId = new URLSearchParams(location.search).get("export");
  try {
    // Whatever it holds, the fragment goes now.
    window.history.replaceState(window.history.state, "", location.pathname + location.search);
    if (location.pathname === "/settings/data" && token && isUuid(exportId))
      transientStore.put("export-token", { exportId, token }, EXPORT_TOKEN_TTL_MS);
  } catch (e) {
    // Never block the app's start (storage refused, a value the store won't take): the link can be opened again.
    console.error("export link: the token was not kept", e);
  }
}

/** The token of the link for this export, if it came in this tab within the last 15 minutes. */
export function exportToken(exportId: string | null | undefined): string | null {
  const stored = transientStore.peek("export-token") as { exportId?: unknown; token?: unknown } | null;
  return stored && stored.exportId === exportId && typeof stored.token === "string" ? stored.token : null;
}

export function forgetExportToken(): void {
  transientStore.remove("export-token");
}
